import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpRight, Link as LinkIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export function HeroSection() {
  const navigate = useNavigate()
  const [productUrl, setProductUrl] = useState('')

  const handleStartWinning = () => {
    const params = productUrl.trim()
      ? `?product_url=${encodeURIComponent(productUrl.trim())}`
      : ''
    navigate(`/register${params}`)
  }

  return (
    <section className="relative overflow-hidden px-4 pb-24 pt-32 sm:px-6 lg:px-8 lg:pb-28 lg:pt-36">
      <div className="pointer-events-none absolute inset-x-0 top-8 h-64 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_64%)]" />
      <div className="pointer-events-none absolute left-[8%] top-24 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-[10%] h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute left-[16%] top-36 h-10 w-10 rounded-full bg-fuchsia-400/35 blur-2xl" />
      <div className="pointer-events-none absolute right-[22%] top-28 h-12 w-12 rounded-full bg-cyan-300/30 blur-2xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="text-center">
          <div className="landing-kicker mx-auto inline-flex animate-fade-in-up items-center rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-200">
            AI creative operating system
          </div>

          <h1 className="landing-title mt-8 animate-fade-in-up text-5xl sm:text-6xl lg:text-7xl">
            <span className="landing-title-base block">
              AI-Powered Creative
            </span>
            <span className="landing-title-highlight block">
              Generation Platform
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-300 opacity-0 animate-fade-in-up sm:text-xl" style={{ animationDelay: '100ms' }}>
            Research competitor ads, uncover market patterns, and turn creative insight into
            winning ads in one focused AI workspace.
          </p>

          <div className="mx-auto mt-12 max-w-3xl opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <p className="mb-3 text-sm font-medium text-slate-400">
              Enter your product URL to get started
            </p>
            <div className="landing-input-shell">
              <div className="landing-input-panel flex flex-col gap-2 rounded-[1.45rem] p-2 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <LinkIcon className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="url"
                    placeholder="https://your-store.com/products/..."
                    value={productUrl}
                    onChange={(e) => setProductUrl(e.target.value)}
                    className="h-14 rounded-[1.1rem] border-0 bg-transparent pl-12 pr-4 text-base text-white shadow-none placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-lg"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleStartWinning()
                      }
                    }}
                  />
                </div>
                <Button
                  size="lg"
                  className="landing-cta-glow h-14 rounded-[1.1rem] px-7 text-base font-semibold text-slate-950 sm:px-8 sm:text-lg"
                  onClick={handleStartWinning}
                >
                  Start Winning
                  <ArrowUpRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3 opacity-0 animate-fade-in" style={{ animationDelay: '350ms' }}>
            {['5 Free Credits', 'No Credit Card', 'Cancel Anytime'].map((badge) => (
              <div
                key={badge}
                className="landing-kicker flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium tracking-[0.18em] text-slate-300"
              >
                <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_16px_rgba(34,211,238,0.9)]" />
                {badge}
              </div>
            ))}
          </div>

          <div className="mx-auto mt-14 grid max-w-5xl gap-4 opacity-0 animate-fade-in-up sm:grid-cols-3" style={{ animationDelay: '450ms' }}>
            {[
              ['Competitive intel', 'Pinpoint hooks, offers, and creative angles that are already converting.'],
              ['Market signals', 'Use AI research to connect audience pain points to campaign-ready ideas.'],
              ['Fast generation', 'Move from URL to production-ready image and video concepts in minutes.'],
            ].map(([title, description]) => (
              <div key={title} className="landing-panel rounded-3xl p-5 text-left">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300/90">{title}</p>
                <p className="mt-3 text-sm leading-6 text-slate-300">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
